import { GfImage } from "@swagger/typescript-fetch-goflyer";

/**
 * Soft-deleted images are filtered out of every generated SDK read (the
 * controller's @Crud filter and the search index exclude them), so listing and
 * restoring them uses the two dedicated endpoints directly. The vendored SDK
 * tarball does not expose them yet - regenerate it (`npm run api`) once the
 * server change is deployed and these helpers can be replaced by SDK calls.
 */
export type DeletedImagesPage = {
  data: GfImage[];
  total: number;
  page: number;
  count: number;
  pageCount: number;
};

const authHeader = (accessToken?: string): Record<string, string> =>
  accessToken ? { Authorization: `Bearer ${accessToken}` } : {};

export async function fetchDeletedImages(
  page: number,
  limit: number,
  accessToken?: string,
  orderBy = "updatedAt,DESC"
): Promise<DeletedImagesPage> {
  const params = new URLSearchParams({
    page: String(page),
    limit: String(limit),
    orderBy,
  });

  const response = await fetch(
    `${
      process.env.REACT_APP_GOFLYER_API_END_POINT
    }/admin-image/deleted?${params.toString()}`,
    { method: "GET", headers: { ...authHeader(accessToken) } }
  );

  if (!response.ok) {
    throw new Error(
      `Failed to load deleted images (${response.status} ${response.statusText})`
    );
  }

  return response.json();
}

export async function restoreImages(
  ids: string[],
  accessToken?: string
): Promise<GfImage[]> {
  if (!ids.length) throw new Error("restoreImages requires at least one id");

  const response = await fetch(
    `${process.env.REACT_APP_GOFLYER_API_END_POINT}/admin-image/restore`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...authHeader(accessToken),
      },
      body: JSON.stringify({ ids }),
    }
  );

  if (!response.ok) {
    throw new Error(
      `Failed to restore images (${response.status} ${response.statusText})`
    );
  }

  return response.json();
}
